import type { SWRConfiguration } from "swr";

import {
  APIListValuesResponse,
  APIPaginatedRequest,
  CountRequest,
  CountResponse,
  EntityAttribute,
  GenericAPIListValuesRequest,
  getAllPagesFetcherV2,
  getFetcherV2,
  listGenericValuesV2RequestURI,
  postFetcherV2,
} from "shared/api/api";
import {
  useAPI,
  useLoadMorePages,
  usePaginatedAPI,
  usePostAPI,
} from "shared/api/hooks";

import {
  countSchemaDefinitionsRequestURI,
  getSchemaDefinitionRequestURI,
  getSchemaImageRequestURI,
  getSchemaRecordRequestURI,
  getSmartGroupingCostEstimateRequestURI,
  listMatchedSchemaRecordsRequestURI,
  listSchemaDataAttributesRequestURI,
  listSchemaDefinitionsRequestURI,
  listSchemaImagesRequestURI,
  listSchemaRecordsCountRequestURI,
  listSchemaRecordsRequestURI,
  listSchemaRelationsRequestURI,
} from "./api";
import {
  ListSchemaDefinitionsRequest,
  ListSchemaRelationsRequest,
  SchemaDefinition,
  SchemaImage,
  SchemaRelation,
  SchemaSmartGroupingCostEstimate,
  SchemaSmartGroupingCostEstimateRequest,
} from "./types";

// Response bodies are unvalidated — `manageFetcherResponse` casts whatever arrived
// to the declared type — so an empty or otherwise non-array body reaches callers
// that call .filter()/.map() on it directly. Degrade to "no data" instead.
const asList = <Item>(data?: Item[]) =>
  Array.isArray(data) ? data : undefined;

export const useSchemaDefinition = (id?: string) =>
  useAPI<string | undefined, SchemaDefinition>(
    getSchemaDefinitionRequestURI,
    id
  );

export const useSmartGroupingCostEstimate = (
  args: { id: string; skipRequest?: boolean },
  body: SchemaSmartGroupingCostEstimateRequest | undefined,
  swrOptions?: SWRConfiguration
) =>
  usePostAPI<typeof args, SchemaSmartGroupingCostEstimate>(
    getSmartGroupingCostEstimateRequestURI,
    args,
    body ?? {},
    swrOptions,
    postFetcherV2
  );

// Every data table, followed page by page, for a caller that needs the registry whole rather than a page at a time —
// a select has no page to turn. Nothing is asked while the caller has no use for the list yet.
export const useAllSchemaDefinitions = (
  isNeeded: boolean,
  args: ListSchemaDefinitionsRequest = {}
) =>
  useAPI<boolean, SchemaDefinition[]>(
    (needed) => (needed ? (listSchemaDefinitionsRequestURI(args) ?? "") : ""),
    isNeeded,
    undefined,
    (path) => getAllPagesFetcherV2<SchemaDefinition>(path)
  );

export const useSchemaDefinitionList = ({
  skipRequest,
  ...args
}: ListSchemaDefinitionsRequest) => {
  const result = usePaginatedAPI<typeof args, SchemaDefinition[]>(
    // The callback has to build the URI from the arguments it is handed, not from `args`: usePaginatedAPI passes its
    // own cursor in, and closing over `args` instead would pin every caller to the first page.
    (paginatedArgs) =>
      listSchemaDefinitionsRequestURI(paginatedArgs, skipRequest),
    args
  );

  return { ...result, data: asList(result.data) };
};

export const useSchemaDefinitionCount = (args: CountRequest) =>
  useAPI<typeof args, CountResponse>(
    countSchemaDefinitionsRequestURI,
    args,
    undefined,
    getFetcherV2
  );

export const useSchemaRelationList = (args: ListSchemaRelationsRequest) =>
  usePaginatedAPI<typeof args, SchemaRelation[]>(
    listSchemaRelationsRequestURI,
    args
  );

export interface ListSchemaRecordsRequest extends APIPaginatedRequest {
  tableType: string;
  name: string;
  assetFilter?: string;
  assetType?: string;
  includeSrcURLs?: boolean;
  preloadAttachments?: boolean;
  skipRequest?: boolean;
}

export const useSchemaRecord = (args: {
  tableType: string;
  name: string;
  id: string;
  includeSrcURLs?: boolean;
}) =>
  useAPI<typeof args, Record<string, unknown>>(getSchemaRecordRequestURI, args);

export const useListSchemaRecords = (args: ListSchemaRecordsRequest) =>
  usePaginatedAPI<ListSchemaRecordsRequest, Record<string, unknown>[]>(
    listSchemaRecordsRequestURI,
    args
  );

// Accumulating variant of useListSchemaRecords for "load more" surfaces: every
// loaded page stays in the returned list. The cursor and page size belong to the
// hook, so the argument type excludes them rather than silently dropping them.
export type LoadMoreSchemaRecordsRequest = Omit<
  ListSchemaRecordsRequest,
  "limit" | "before" | "after"
>;

export const useLoadMoreSchemaRecords = (
  args: LoadMoreSchemaRecordsRequest,
  initialPageSize: number,
  loadMorePageSize: number
) =>
  useLoadMorePages<LoadMoreSchemaRecordsRequest, Record<string, unknown>>(
    listSchemaRecordsRequestURI,
    args,
    initialPageSize,
    loadMorePageSize
  );

export interface ListMatchedSchemaRecordsRequest extends APIPaginatedRequest {
  tableType: string;
  name: string;
  id: string;
  otherSchemaName: string;
}

// useListMatchedSchemaRecords loads the AI-matched subset for the "Only show
// relevant" checked state on the event details page. Each row in the response
// carries `eventDetails` + `matchedEventDetails` maps that drive the yellow
// match-highlight rendering.
export const useListMatchedSchemaRecords = (
  args: ListMatchedSchemaRecordsRequest
) =>
  usePaginatedAPI<ListMatchedSchemaRecordsRequest, Record<string, unknown>[]>(
    listMatchedSchemaRecordsRequestURI,
    args
  );

export const useListSchemaRecordsCount = (args: {
  tableType: string;
  name: string;
  filter?: string;
  assetFilter?: string;
  assetType?: string;
}) =>
  useAPI<typeof args, CountResponse>(listSchemaRecordsCountRequestURI, args);

export const useSchemaDataValues = ({
  tableType,
  name,
  skipRequest,
  ...args
}: {
  tableType: string;
  name: string;
  fieldName: string;
  like?: string;
  limit?: number;
  skipRequest?: boolean;
}) =>
  useAPI<GenericAPIListValuesRequest, APIListValuesResponse>(
    (params) => (skipRequest ? null : listGenericValuesV2RequestURI(params)),
    { resourceEndpoint: `${tableType}/${name}`, ...args }
  );

export const useSchemaAttributes = (args: {
  tableType: string;
  name: string;
}) => {
  const result = useAPI<typeof args, EntityAttribute[]>(
    listSchemaDataAttributesRequestURI,
    args
  );

  return { ...result, data: asList(result.data) };
};

export const useSchemaImages = (definitionID?: string) =>
  useAPI<string | undefined, SchemaImage[]>(
    listSchemaImagesRequestURI,
    definitionID
  );

export const useSchemaImage = (definitionID?: string, imageID?: string) =>
  useAPI<{ definitionID?: string; imageID?: string }, SchemaImage>(
    getSchemaImageRequestURI,
    { definitionID, imageID }
  );
